/**
 * @file Sandbox UI panel.
 *
 * Builds the DOM, wires up events, and exposes methods for the game to
 * call (open, close, refresh). The panel is an overlay on the canvas —
 * it does not interfere with gameplay.
 */

import { ENEMY_KEYS, TerrainMode, sandboxState } from './SandboxConfig.js';
import { ENEMIES } from '../config/Enemies.js';
import { WEAPONS } from '../config/Weapons.js';
import { inspectWeaponTuning } from '../config/WeaponTuning.js';

/**
 * @param {HTMLElement} container Root element to attach the panel to.
 * @returns {HTMLElement} The panel element (for toggling visibility).
 */

/** Player-carryable weapon keys exposed by the shared weapon debug surface. */
function playerWeaponKeys() {
  return inspectWeaponTuning().map((weapon) => weapon.key);
}

export function buildSandboxPanel(container) {
  const panel = document.createElement('div');
  panel.className = 'sandbox-panel';
  panel.setAttribute('data-sandbox', 'panel');

  panel.innerHTML = `
    <div class="sandbox-panel__header">
      <span class="sandbox-panel__title">Sandbox</span>
      <button class="sandbox-panel__close" data-sand="close">&times;</button>
    </div>
    <div class="sandbox-panel__body">

      <!-- Toggle -->
      <div class="sandbox-section">
        <div class="sandbox-toggle">
          <span class="sandbox-toggle__label">Sandbox Mode</span>
          <label class="sandbox-toggle__switch">
            <input type="checkbox" data-sand="toggle">
            <span class="sandbox-toggle__track"></span>
            <span class="sandbox-toggle__thumb"></span>
          </label>
        </div>
      </div>

      <!-- Counter -->
      <div class="sandbox-section">
        <div class="sandbox-counter">
          <span class="sandbox-counter__label">Enemies Active</span>
          <span class="sandbox-counter__value" data-sand="count">0</span>
        </div>
      </div>

      <!-- Enemy toggles -->
      <div class="sandbox-section">
        <div class="sandbox-section__title">Enemy Types</div>
        <div class="sandbox-enemy-list" data-sand="enemyList"></div>
      </div>

      <!-- Spray -->
      <div class="sandbox-section">
        <button class="sandbox-spray-btn" data-sand="spray">
          Spray Mode (click map)
        </button>
      </div>

      <!-- Terrain -->
      <div class="sandbox-section">
        <div class="sandbox-section__title">Terrain Mode</div>
        <div class="sandbox-terrain-group" data-sand="terrainGroup">
          <button class="sandbox-terrain-btn" data-sand="terrain" data-value="flat">Flat</button>
          <button class="sandbox-terrain-btn" data-sand="terrain" data-value="flat_obstacles">Flat + Obstacles</button>
          <button class="sandbox-terrain-btn" data-sand="terrain" data-value="full">Full</button>
        </div>
      </div>

      <!-- Weapon switcher -->
      <div class="sandbox-section">
        <div class="sandbox-section__title">Weapon</div>
        <div class="sandbox-terrain-group" data-sand="weaponGroup">
          ${playerWeaponKeys()
            .map((key) => `<button class="sandbox-terrain-btn" data-sand="weapon" data-value="${key}">${WEAPONS[key].name}</button>`)
            .join('')}
        </div>
      </div>

      <!-- Cheats -->
      <div class="sandbox-section">
        <div class="sandbox-section__title">Cheats</div>
        <div class="sandbox-cheat-list" data-sand="cheatList">
          <div class="sandbox-toggle">
            <span class="sandbox-toggle__label">God mode</span>
            <label class="sandbox-toggle__switch">
              <input type="checkbox" data-cheat="godMode">
              <span class="sandbox-toggle__track"></span>
              <span class="sandbox-toggle__thumb"></span>
            </label>
          </div>
          <div class="sandbox-toggle">
            <span class="sandbox-toggle__label">Infinite ammo</span>
            <label class="sandbox-toggle__switch">
              <input type="checkbox" data-cheat="infiniteAmmo">
              <span class="sandbox-toggle__track"></span>
              <span class="sandbox-toggle__thumb"></span>
            </label>
          </div>
          <div class="sandbox-toggle">
            <span class="sandbox-toggle__label">One-shot kills</span>
            <label class="sandbox-toggle__switch">
              <input type="checkbox" data-cheat="oneShotKills">
              <span class="sandbox-toggle__track"></span>
              <span class="sandbox-toggle__thumb"></span>
            </label>
          </div>
        </div>
      </div>

      <!-- Enemy Info -->
      <div class="sandbox-section">
        <div class="sandbox-info" data-sand="info">
          <div class="sandbox-info__name" data-sand="infoName">—</div>
          <div id="sand-info-body"></div>
        </div>
      </div>

      <!-- Actions -->
      <div class="sandbox-section">
        <div class="sandbox-actions">
          <button class="sandbox-btn" data-sand="refillTimer">Refill Timer</button>
          <button class="sandbox-btn sandbox-btn--danger" data-sand="killEnemies">Kill All Enemies</button>
        </div>
        <div class="sandbox-actions">
          <button class="sandbox-btn" data-sand="reset">Reset</button>
          <button class="sandbox-btn sandbox-btn--danger" data-sand="clearAll">Clear Spawned</button>
        </div>
      </div>

      <!-- Hint -->
      <div class="sandbox-hint">
        Press <kbd>,</kbd> for the debug window
      </div>
    </div>
  `;

  return panel;
}

/**
 * Wire up all panel events.
 * @param {HTMLElement} panel
 * @param {object} handlers
 * @param {(enabled: boolean) => void} handlers.onToggle
 * @param {(spray: boolean) => void} handlers.onSpray
 * @param {(type: string) => void} handlers.onSprayType
 * @param {(terrain: string) => void} handlers.onTerrain
 * @param {() => void} handlers.onReset
 * @param {() => void} handlers.onClearAll
 * @param {(key: string, enabled: boolean) => void} handlers.onCheatToggle
 * @param {() => void} handlers.onKillEnemies
 * @param {() => void} handlers.onRefillTimer
 * @param {(weaponKey: string) => void} handlers.onGiveWeapon
 * @param {() => void} handlers.onClose
 * @param {(enemyKey: string) => void} handlers.onEnemyToggle
 * @param {(entity: import('../ecs/Entity.js').Entity) => void} handlers.onEntityClick
 */
export function wireSandboxPanel(panel, handlers) {
  const el = panel.querySelector.bind(panel);

  // --- Toggle ---
  const toggle = /** @type {HTMLInputElement} */ (el('[data-sand="toggle"]'));
  toggle.addEventListener('change', () => handlers.onToggle(toggle.checked));

  // --- Close ---
  const closeBtn = /** @type {HTMLElement} */ (el('[data-sand="close"]'));
  closeBtn.addEventListener('click', () => handlers.onClose());

  // --- Spray ---
  const sprayBtn = /** @type {HTMLElement} */ (el('[data-sand="spray"]'));
  sprayBtn.addEventListener('click', () => {
    handlers.onSpray(!sandboxState.sprayMode);
  });

  // --- Terrain ---
  panel.querySelectorAll('[data-sand="terrain"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const value = btn.getAttribute('data-value');
      if (value) handlers.onTerrain(value);
    });
  });

  // --- Reset / Clear ---
  const resetBtn = /** @type {HTMLElement} */ (el('[data-sand="reset"]'));
  resetBtn.addEventListener('click', () => handlers.onReset());

  const clearBtn = /** @type {HTMLElement} */ (el('[data-sand="clearAll"]'));
  clearBtn.addEventListener('click', () => handlers.onClearAll());

  // --- Cheats / one-shot actions ---
  const cheatList = /** @type {HTMLElement} */ (el('[data-sand="cheatList"]'));
  cheatList.addEventListener('change', (e) => {
    const target = /** @type {HTMLInputElement} */ (e.target);
    const key = target.getAttribute('data-cheat');
    if (key) handlers.onCheatToggle(key, target.checked);
  });

  const killBtn = /** @type {HTMLElement} */ (el('[data-sand="killEnemies"]'));
  killBtn.addEventListener('click', () => handlers.onKillEnemies());

  const refillBtn = /** @type {HTMLElement} */ (el('[data-sand="refillTimer"]'));
  refillBtn.addEventListener('click', () => handlers.onRefillTimer());

  // --- Weapon switcher ---
  panel.querySelectorAll('[data-sand="weapon"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.getAttribute('data-value');
      if (!key) return;
      handlers.onGiveWeapon(key);
      panel.querySelectorAll('[data-sand="weapon"]').forEach((b) => b.classList.toggle('sandbox-terrain-btn--active', b === btn));
    });
  });

  // --- Enemy toggles (delegated) ---
  const enemyList = /** @type {HTMLElement} */ (el('[data-sand="enemyList"]'));
  enemyList.addEventListener('change', (e) => {
    const target = /** @type {HTMLInputElement} */ (e.target);
    if (target.type === 'checkbox') {
      const key = target.getAttribute('data-enemy');
      if (key) handlers.onEnemyToggle(key);
    }
  });

  // Click an enemy row (outside the checkbox) to select spray type.
  enemyList.addEventListener('click', (e) => {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.closest('input') || target.closest('label')) return;
    const row = target.closest('[data-enemy]');
    if (!row) return;
    const key = row.getAttribute('data-enemy');
    if (key) handlers.onSprayType(key);
  });

  // --- Entity click (delegated) ---
  panel.addEventListener('click', (e) => {
    const infoBtn = /** @type {HTMLElement} */ (e.target.closest('[data-sand="showInfo"]'));
    if (infoBtn) {
      const entityId = Number(infoBtn.getAttribute('data-entity-id'));
      if (entityId) handlers.onEntityClick(entityId);
    }
  });
}

/**
 * Build the enemy toggle rows.
 * @param {HTMLElement} container
 */
export function renderEnemyToggles(container) {
  container.innerHTML = '';
  for (const key of ENEMY_KEYS) {
    const arch = ENEMIES[key];
    if (!arch) continue;

    const row = document.createElement('div');
    row.className = 'sandbox-enemy-row';
    row.dataset.enemy = key;
    if (sandboxState.sprayType === key) {
      row.classList.add('sandbox-enemy-row--selected');
    }

    row.innerHTML = `
      <span class="sandbox-enemy-row__color" style="background: #${arch.color.toString(16).padStart(6, '0')}"></span>
      <span class="sandbox-enemy-row__name">${arch.name}</span>
      <label class="sandbox-toggle__switch" style="width:36px;height:20px">
        <input type="checkbox" data-enemy="${key}" ${sandboxState.enemyToggles[key] ? 'checked' : ''}>
        <span class="sandbox-toggle__track" style="width:36px;height:20px;border-radius:10px"></span>
        <span class="sandbox-toggle__thumb" style="width:14px;height:14px;top:3px;left:3px"></span>
      </label>
      <span class="sandbox-enemy-row__count" data-sand="enemyCount_${key}">0</span>
    `;

    // Apply disabled style
    if (!sandboxState.enemyToggles[key]) {
      row.classList.add('sandbox-enemy-row--disabled');
    }

    container.appendChild(row);
  }
}

/**
 * Update the terrain buttons' active state.
 * @param {HTMLElement} container
 */
export function updateTerrainButtons(container) {
  container.querySelectorAll('[data-sand="terrain"]').forEach((btn) => {
    const value = btn.getAttribute('data-value');
    btn.classList.toggle('sandbox-terrain-btn--active', value === sandboxState.terrainMode);
  });
}

/**
 * Update the enemy counter and per-type counts.
 * @param {HTMLElement} panel
 * @param {number} totalAlive
 * @param {Record<string, number>} counts Per-type counts
 */
export function updateCounters(panel, totalAlive, counts = {}) {
  const countEl = /** @type {HTMLElement} */ (panel.querySelector('[data-sand="count"]'));
  if (countEl) countEl.textContent = String(totalAlive);

  for (const key of ENEMY_KEYS) {
    const el = panel.querySelector(`[data-sand="enemyCount_${key}"]`);
    if (el) el.textContent = String(counts[key] ?? 0);
  }
}

/**
 * Show or hide the panel.
 * @param {HTMLElement} panel
 * @param {boolean} open
 */
export function setPanelOpen(panel, open) {
  if (open) {
    panel.classList.add('sandbox-panel--open');
  } else {
    panel.classList.remove('sandbox-panel--open');
  }
}

/**
 * Update spray button state.
 * @param {HTMLElement} panel
 */
export function updateSprayButton(panel) {
  const btn = /** @type {HTMLElement} */ (panel.querySelector('[data-sand="spray"]'));
  if (btn) {
    btn.classList.toggle('sandbox-spray-btn--active', sandboxState.sprayMode);
    btn.textContent = sandboxState.sprayMode
      ? 'Stop Spray (click map)'
      : 'Spray Mode (click map)';
  }
}

/**
 * Show enemy info panel.
 * @param {HTMLElement} panel
 * @param {import('../config/Enemies.js').EnemyArchetype} arch
 * @param {number} [health] Current health (if entity)
 */
export function showEnemyInfo(panel, arch, health) {
  const info = /** @type {HTMLElement} */ (panel.querySelector('[data-sand="info"]'));
  const nameEl = /** @type {HTMLElement} */ (panel.querySelector('[data-sand="infoName"]'));
  const bodyEl = /** @type {HTMLElement} */ (panel.querySelector('#sand-info-body'));

  if (!info || !nameEl || !bodyEl) return;

  nameEl.textContent = arch.name;

  let html = '';
  html += `<div class="sandbox-info__row"><span class="sandbox-info__key">Behavior</span><span>${arch.behavior}</span></div>`;
  html += `<div class="sandbox-info__row"><span class="sandbox-info__key">Health</span><span>${health ?? arch.maxHealth}${health !== undefined ? '/' + arch.maxHealth : ''}</span></div>`;
  html += `<div class="sandbox-info__row"><span class="sandbox-info__key">Speed</span><span>${arch.speed}</span></div>`;
  html += `<div class="sandbox-info__row"><span class="sandbox-info__key">Damage</span><span>${arch.damage}</span></div>`;
  html += `<div class="sandbox-info__row"><span class="sandbox-info__key">Attack Range</span><span>${arch.attackRange}</span></div>`;
  html += `<div class="sandbox-info__row"><span class="sandbox-info__key">Seek Range</span><span>${arch.seekRange}</span></div>`;
  if (arch.weapon) {
    html += `<div class="sandbox-info__row"><span class="sandbox-info__key">Weapon</span><span>${arch.weapon}</span></div>`;
  }
  if (arch.explosive) {
    html += `<div class="sandbox-info__row"><span class="sandbox-info__key">Explosive</span><span>R:${arch.explosive.radius} D:${arch.explosive.damage}</span></div>`;
  }
  html += `<div class="sandbox-info__row"><span class="sandbox-info__key">Score</span><span>${arch.scoreValue}</span></div>`;

  bodyEl.innerHTML = html;
  info.classList.add('sandbox-info--visible');
}

/**
 * Hide enemy info panel.
 * @param {HTMLElement} panel
 */
export function hideEnemyInfo(panel) {
  const info = /** @type {HTMLElement} */ (panel.querySelector('[data-sand="info"]'));
  if (info) info.classList.remove('sandbox-info--visible');
}
